import type { Event, Graph, WithContext } from 'schema-dts'
import type { EventDescription } from '~/composables/types/event'

const MAX_SCHEMA_REVIEWS = 10

const useEventSchema = (events: EventDescription[]): any => {
  const schema = ref<Graph[] | undefined>(undefined)
  const script = computed(() => ({
    key: 'event-schema',
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))

  const fullLocalizedUrl = useFullLocalizedUrl()

  const createSchema = (): Event[] => {
    // Events without an upcoming date get no markup: Google treats invented
    // dates as misleading structured data.
    return events
      .filter((event) => eventGetters.getNextDateAvailable(event))
      .map((event): WithContext<Event> => {
        const tz = eventGetters.getTimezone(event)
        const url = fullLocalizedUrl(
          `/${
            eventGetters.isGiftcardEvent(event) ? 'giftcard' : 'e'
          }/${eventGetters.getPermalink(event)}/`
        )

        const reviewSchemaArr = [...eventGetters.getReviewsList(event)]
          .sort((a, b) =>
            String(reviewGetters.getCreatedAt(b) ?? '').localeCompare(
              String(reviewGetters.getCreatedAt(a) ?? '')
            )
          )
          .slice(0, MAX_SCHEMA_REVIEWS)
          .map((review) => {
            const datePublished = formatSchemaDate(
              reviewGetters.getCreatedAt(review),
              tz
            )

            return {
              '@type': 'Review',
              reviewRating: {
                '@type': 'Rating',
                ratingValue: reviewGetters.getReviewRating(review),
              },
              reviewBody: reviewGetters.getReview(review),
              author: {
                '@type': 'Person',
                name: reviewGetters.getAuthor(review),
              },
              ...(datePublished && { datePublished }),
            }
          })

        const endDate = formatSchemaDate(
          eventGetters.getNextDateAvailableEnd(event),
          tz
        )

        const partnerPath = supplierGetters.getKonfettiProfilePath(
          eventGetters.getSupplier(event)
        )
        const partnerUrl = fullLocalizedUrl(partnerPath)
        const partnerId = getSchemaBusinessId(partnerPath)

        const obj: WithContext<Event> = {
          '@type': 'Event',
          name: eventGetters.getTitle(event),
          url,
          startDate: formatSchemaDate(
            eventGetters.getNextDateAvailable(event),
            tz
          ),
          ...(endDate && { endDate }),
          eventAttendanceMode:
            schemaEventAttendanceModes[eventGetters.getAttendanceMode(event)],
          // TODO dynamic eventStatus
          eventStatus: 'https://schema.org/EventScheduled',
          image: eventGetters.getSchemaImages(event),
          description: eventGetters.getSubtitle(event),
          offers: {
            '@type': 'Offer',
            availability: getSchemaOfferAvailability(
              event.next_instance?.availableTicketsQuantity
            ),
            price: eventGetters.getDefaultPriceAmount(event) / 100,
            priceCurrency: eventGetters.getDefaultPriceCurrency(event),
            priceSpecification: eventGetters.getSchemaPriceSpecification(event),
            url,
          },
          performer: {
            '@type': 'PerformingGroup',
            name: eventGetters.getSupplierName(event),
          },
          // Same @id as the partner page's business node, so Google ties the
          // event to that business.
          organizer: {
            '@type': 'Organization',
            ...(partnerId && { '@id': partnerId }),
            name: eventGetters.getSupplierName(event),
            url: partnerUrl,
          },
          ...(eventGetters.getReviewsTotal(event) &&
            eventGetters.getReviewsTotal(event) > 0 && {
              ...(reviewSchemaArr.length > 0 && { review: reviewSchemaArr }),
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: eventGetters.getReviewsAverage(event, 'en-US'),
                bestRating: 5,
                worstRating: 1,
                ratingCount: eventGetters.getReviewsTotal(event),
              },
            }),
        }

        if (eventGetters.getAttendanceMode(event) !== 'ONLINE') {
          const [lat, lng] = eventGetters.getCoordinates(event) ?? []
          const geo = getSchemaGeo(lat, lng)

          obj.location = {
            '@type': 'Place',
            name: eventGetters.getSupplierName(event),
            address: getSchemaPostalAddress({
              formatted: eventGetters.getFormattedAddress(event),
              zipcode: eventGetters.getAddressZipcode(event),
              locality: eventGetters.getAddressLocalityName(event),
            }),
            ...(geo && { geo }),
          }
        } else {
          obj.location = {
            '@type': 'VirtualLocation',
            url,
          }
        }

        return obj
      })
  }

  schema.value = { '@context': 'https://schema.org', '@graph': createSchema() }

  return {
    schema,
    script,
  }
}

export default useEventSchema
